import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  ArrowLeft, Archive, Bot, ChevronDown, ExternalLink, Image as ImageIcon, Lock, Plus, RotateCcw, Settings as SettingsIcon, Trash2, X,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { RichTextField } from "@/components/ui/rich-text-field"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ClipActivityComposer } from "@/components/clips/activity/ClipActivityComposer"
import { ClipActivityFeed } from "@/components/clips/activity/ClipActivityFeed"
import { ClipStatusBadge } from "@/components/clips/ClipStatusBadge"
import { OneTimeUseBadge } from "@/components/clips/OneTimeUseBadge"
import { ClipThumbnailPickerDialog } from "@/components/clips/ClipThumbnailPickerDialog"
import { ClipPreviewDialog } from "@/components/clips/ClipPreviewDialog"
import { DeleteClipDialog } from "@/components/clips/DeleteClipDialog"
import { cn } from "@/lib/utils"
import type { ClipDetail, TagRef } from "@/components/clips/types"
import type { CurrentUser, PageProps } from "@/types/inertia"

type TabKey = "details" | "activity"

type ShowProps = PageProps<{
  clip: ClipDetail
  all_tags: TagRef[]
  folders: string[]
}>

function tabFromUrl(url: string): TabKey {
  const q = url.split("?")[1] ?? ""
  const t = new URLSearchParams(q).get("tab")
  if (t === "activity") return t
  return "details"
}

export default function ClipShow() {
  const { props, url } = usePage<ShowProps>()
  const { clip } = props
  const activeTab = tabFromUrl(url)

  return (
    <>
      <Head title={clip.title || clip.current_filename || "Clip"}>
        <meta name="description" content={clip.short_description ?? "B-roll clip details and metadata."} />
        <meta property="og:title" content={clip.title || clip.current_filename || "Clip"} />
        <meta property="og:description" content={clip.short_description ?? "B-roll clip details and metadata."} />
      </Head>
      <AppShell maxWidth="6xl">
        <PageHeader
          title={clip.title || clip.current_filename || "Untitled clip"}
          description={clip.short_description || <span className="text-ink-muted">No short description yet.</span>}
          actions={<HeaderActions clip={clip} />}
          tabs={<ClipTabs clipId={clip.id} active={activeTab} commentCount={clip.events_comment_count} />}
        />

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-6">
            {activeTab === "details" && <DetailsTab clip={clip} />}
            {activeTab === "activity" && <ActivityTab clip={clip} currentUser={props.current_user} />}
          </div>
          <Sidebar clip={clip} />
        </div>
      </AppShell>
    </>
  )
}

// --- Header actions dropdown ------------------------------------------------

function HeaderActions({ clip }: { clip: ClipDetail }) {
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  return (
    <div className="flex items-center gap-2">
      <Link href="/clips" className="inline-flex items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display">
        <ArrowLeft className="h-4 w-4" /> Back to library
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="secondary">
            <SettingsIcon className="mr-1 h-4 w-4" /> Actions <ChevronDown className="ml-1 h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={() => router.post(`/clips/${clip.id}/extract_thumbnails`, {}, { preserveScroll: true })}>
            <ImageIcon /> {clip.has_thumbnails ? "Refresh thumbnails" : "Get thumbnail & enrich"}
          </DropdownMenuItem>
          {clip.has_thumbnails && clip.status === "needs_processing" && (
            <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/mark_for_enrichment`, {}, { preserveScroll: true })}>
              <Bot /> Queue for enrichment
            </DropdownMenuItem>
          )}
          {clip.status === "queued_for_enrichment" && (
            <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/cancel_enrichment`, {}, { preserveScroll: true })}>
              <X /> Cancel enrichment
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {clip.status !== "deprecated" && clip.status !== "missing" && (
            clip.one_time_use ? (
              <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/unmark_one_time_use`, {}, { preserveScroll: true })}>
                <RotateCcw /> Unmark one-time-use
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/mark_one_time_use`, {}, { preserveScroll: true })}>
                <Lock /> Mark as one-time-use
              </DropdownMenuItem>
            )
          )}
          {clip.status === "deprecated" ? (
            <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/undeprecate`, {}, { preserveScroll: true })}>
              <RotateCcw /> Un-deprecate
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem destructive onSelect={() => router.patch(`/clips/${clip.id}/deprecate`, {}, { preserveScroll: true })}>
              <Archive /> Deprecate
            </DropdownMenuItem>
          )}
          <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
            <Trash2 /> Delete clip
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteClipDialog clip={clip} open={deleteOpen} onOpenChange={setDeleteOpen} />
    </div>
  )
}

// --- Tabs strip -------------------------------------------------------------

function ClipTabs({
  clipId, active, commentCount,
}: {
  clipId: number
  active: TabKey
  commentCount: number
}) {
  const tabs: Array<{ key: TabKey; label: string; count?: number; href: string }> = [
    { key: "details",  label: "Details",  href: `/clips/${clipId}` },
    { key: "activity", label: "Activity", count: commentCount, href: `/clips/${clipId}?tab=activity` },
  ]
  return (
    <nav className="flex items-end gap-6 border-b border-hairline">
      {tabs.map((tab) => {
        const isActive = tab.key === active
        return (
          <Link
            key={tab.key}
            href={tab.href}
            preserveScroll
            className={cn(
              "-mb-px cursor-pointer border-b-2 px-1 py-3 text-sm no-underline",
              isActive
                ? "border-accent font-medium text-accent-display"
                : "border-transparent text-ink-body hover:text-ink-display",
            )}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span className={cn(
                "ml-2 inline-flex items-center rounded-full px-1.5 text-[10px] font-semibold",
                isActive ? "bg-accent-faded text-accent-display" : "bg-surface text-ink-muted",
              )}>{tab.count}</span>
            )}
          </Link>
        )
      })}
    </nav>
  )
}

// --- Details tab — metadata form (no container, no heading) -----------------

function DetailsTab({ clip }: { clip: ClipDetail }) {
  const [title, setTitle] = React.useState(clip.title ?? "")
  const [shortDesc, setShortDesc] = React.useState(clip.short_description ?? "")
  const [longDesc, setLongDesc] = React.useState(clip.long_description ?? "")
  const [tags, setTags] = React.useState(clip.tag_names.join(", "))
  const [triggers, setTriggers] = React.useState(clip.auto_match_triggers.join(", "))
  const [recordedDate, setRecordedDate] = React.useState(clip.recorded_date ?? "")
  const [saving, setSaving] = React.useState(false)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    router.patch(`/clips/${clip.id}`, {
      title,
      short_description: shortDesc,
      long_description: longDesc,
      tag_names: tags.split(",").map((t) => t.trim()).filter(Boolean),
      auto_match_triggers: triggers.split(",").map((t) => t.trim()).filter(Boolean),
      recorded_date: recordedDate,
    }, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="title">Title</label>
        <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
      </div>

      <div>
        <label htmlFor="short_desc">Short description</label>
        <Input id="short_desc" value={shortDesc} onChange={(e) => setShortDesc(e.target.value)} className="mt-1" />
      </div>

      <div>
        <label htmlFor="long_desc">Long description</label>
        <div id="long_desc" className="mt-1">
          <RichTextField
            defaultValue={clip.long_description ?? ""}
            onChange={setLongDesc}
            placeholder="Describe what the clip shows — markdown supported."
          />
        </div>
      </div>

      <div>
        <label htmlFor="tags">Tags (comma-separated)</label>
        <Input id="tags" value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1" />
      </div>

      <div>
        <label htmlFor="triggers">Auto-match triggers (comma-separated)</label>
        <Input id="triggers" value={triggers} onChange={(e) => setTriggers(e.target.value)} className="mt-1" />
        <p className="mt-1 text-xs text-ink-muted">
          Exact phrases (case-insensitive) that hard-match this clip during visuals planning. Use sparingly.
        </p>
      </div>

      <div>
        <label htmlFor="recorded">Recorded date</label>
        <Input id="recorded" type="date" value={recordedDate} onChange={(e) => setRecordedDate(e.target.value)} className="mt-1 w-48" />
      </div>

      <div>
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </form>
  )
}

// --- Activity tab -----------------------------------------------------------

function ActivityTab({ clip, currentUser }: { clip: ClipDetail; currentUser: CurrentUser }) {
  const composerRef = React.useRef<HTMLDivElement>(null)
  const jumpToComposer = () => {
    const node = composerRef.current
    if (!node) return
    node.scrollIntoView({ behavior: "smooth", block: "center" })
    window.setTimeout(() => {
      const editable = node.querySelector<HTMLElement>(".ProseMirror")
      editable?.focus()
    }, 400)
  }

  return (
    <div>
      <div className="mb-10 flex items-center justify-between gap-3">
        <h2>Activity</h2>
        <Button type="button" size="sm" variant="soft" onClick={jumpToComposer}>
          <Plus size={14} /> Comment
        </Button>
      </div>

      <ClipActivityFeed events={clip.events} clipId={clip.id} currentUser={currentUser} />

      <div ref={composerRef} className="mt-10">
        <ClipActivityComposer clipId={clip.id} />
      </div>
    </div>
  )
}

// --- Sidebar — single container, hover-flip thumbnail + metadata stack ------

function Sidebar({ clip }: { clip: ClipDetail }) {
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [previewOpen, setPreviewOpen] = React.useState(false)
  const [previewIndex, setPreviewIndex] = React.useState(0)

  return (
    <aside className="rounded-lg border border-hairline bg-surface p-4">
      <RotatingThumbnail
        clip={clip}
        onClick={(index) => {
          setPreviewIndex(index)
          setPreviewOpen(true)
        }}
      />
      <div className="mt-2 text-center">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={clip.thumbnails.length === 0}
          className="cursor-pointer text-xs text-accent-display hover:underline disabled:cursor-not-allowed disabled:text-ink-muted disabled:no-underline"
        >
          {clip.thumbnails.length === 0 ? "No thumbnails yet" : "Select primary →"}
        </button>
      </div>

      <dl className="mt-4 space-y-3 text-sm">
        <SidebarRow label="Status">
          <div className="flex flex-wrap items-center gap-1">
            <ClipStatusBadge status={clip.status} />
            {clip.one_time_use && <OneTimeUseBadge />}
          </div>
        </SidebarRow>

        <SidebarRow label="Clip type">
          <span className="text-ink-display">{clip.clip_type?.name ?? "—"}</span>
        </SidebarRow>

        <SidebarRow label="Dropbox location">
          <span className="block break-words text-ink-display">{clip.current_dropbox_path ?? "(unknown)"}</span>
          {clip.dropbox_web_url && (
            <a
              href={clip.dropbox_web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-accent-display no-underline hover:underline"
            >
              Open in Dropbox <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </SidebarRow>

        <SidebarRow label="Duration">
          <span className="text-ink-display">{formatDuration(clip.duration_seconds)}</span>
        </SidebarRow>
      </dl>

      <ClipThumbnailPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        clipId={clip.id}
        thumbnails={clip.thumbnails}
      />

      <ClipPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        thumbnails={clip.thumbnails}
        initialIndex={previewIndex}
        title={clip.title || clip.current_filename || "Clip preview"}
      />
    </aside>
  )
}

function SidebarRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-hairline pt-3 first:border-t-0 first:pt-0">
      <dt className="text-[10px] uppercase tracking-wider text-ink-muted">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  )
}

const CYCLE_INTERVAL_MS = 900

function RotatingThumbnail({ clip, onClick }: { clip: ClipDetail; onClick: (index: number) => void }) {
  const [index, setIndex] = React.useState(() => {
    const primaryIndex = clip.thumbnails.findIndex((t) => t.is_primary)
    return primaryIndex >= 0 ? primaryIndex : 0
  })

  React.useEffect(() => {
    if (clip.thumbnails.length < 2) return
    const interval = window.setInterval(() => {
      setIndex((i) => (i + 1) % clip.thumbnails.length)
    }, CYCLE_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [clip.thumbnails.length])

  const displayed = clip.thumbnails[index]

  if (!displayed) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-hairline bg-page text-xs text-ink-muted">
        No thumbnails yet
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onClick(index)}
      aria-label="View preview images larger"
      className="relative block aspect-video w-full cursor-pointer overflow-hidden rounded-md bg-page"
    >
      {displayed.url ? (
        <img src={displayed.url} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-xs text-ink-muted">no image</div>
      )}
      {clip.thumbnails.length > 1 && (
        <div className="absolute bottom-1 right-1 flex gap-0.5">
          {clip.thumbnails.map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1 w-1 rounded-full",
                i === index ? "bg-page" : "bg-page/40",
              )}
            />
          ))}
        </div>
      )}
    </button>
  )
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "Not enriched yet"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}
